<template>
  <div id="app">
    <!-- En-tête commun aux pages intérieures (l'accueil a sa propre barre) -->
    <header v-if="!isHome" class="app-header" :class="{ scrolled }">
      <img
        class="logo"
        alt="HayMiutyun"
        src="./assets/HayMiutyunRemove.png"
        role="link"
        tabindex="0"
        @click="goHome"
        @keydown.enter="goHome"
      />
    </header>

    <main
      id="main-content"
      class="app-main with-tabs"
      :class="{ 'with-header': !isHome }"
    >
      <router-view v-slot="{ Component, route: r }">
        <transition name="page" mode="out-in" @after-leave="scrollTop">
          <component :is="Component" :key="r.path" />
        </transition>
      </router-view>
    </main>

    <!-- Tab bar : les 5 pages principales -->
    <nav class="tabbar" aria-label="Navigation principale">
      <router-link
        v-for="t in tabs"
        :key="t.key"
        :to="t.to"
        class="tab"
        :class="{ active: isActive(t) }"
        :aria-label="t.label"
        :title="t.label"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <template v-if="t.key === 'home'">
            <path d="M3 11.5 12 4l9 7.5" />
            <path d="M5.5 10v9.5h13V10" />
            <path d="M10 19.5v-5h4v5" />
          </template>
          <template v-else-if="t.key === 'events'">
            <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
            <path d="M3.5 10h17M8 3v4M16 3v4" />
          </template>
          <template v-else-if="t.key === 'help'">
            <path d="M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20Z" />
          </template>
          <template v-else-if="t.key === 'search'">
            <circle cx="11" cy="11" r="6.5" />
            <path d="m20 20-4.2-4.2" />
          </template>
          <template v-else>
            <circle cx="12" cy="8.5" r="3.8" />
            <path d="M4.5 20.5c.8-4 4-6 7.5-6s6.7 2 7.5 6" />
          </template>
        </svg>
      </router-link>
    </nav>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"

const router = useRouter()
const route = useRoute()

const isHome = computed(() => route.path === "/")
const scrolled = ref(false)

const tabs = computed(() => {
  // dépend de la route pour se recalculer après une connexion / déconnexion
  void route.path
  const logged = !!localStorage.getItem("user")
  return [
    { key: "home", label: "Accueil", to: "/", match: ["/"] },
    { key: "events", label: "Événements", to: "/events", match: ["/events", "/createEvents"] },
    { key: "help", label: "Demandes d'aide", to: "/help", match: ["/help", "/requests", "/publish"] },
    { key: "search", label: "Recherche", to: "/search", match: ["/search", "/profile"] },
    {
      key: "account",
      label: "Mon compte",
      to: logged ? "/account" : "/login",
      match: ["/account", "/my-requests", "/login", "/register"],
    },
  ]
})

const isActive = (t) =>
  t.match.some((p) =>
    p === "/" ? route.path === "/" : route.path === p || route.path.startsWith(p + "/")
  )

const goHome = () => {
  router.push("/")
}

const scrollTop = () => {
  window.scrollTo({ top: 0, behavior: "auto" })
}

const onScroll = () => {
  scrolled.value = window.scrollY > 8
}

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true })
  onScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll)
})
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Instrument+Sans:wght@400;500;600;700&family=Noto+Serif+Armenian:wght@400;700&display=swap");

:root {
  --hm-red: #d90012;
  --hm-red-deep: #8f0010;
  --hm-blue: #0033a0;
  --hm-orange: #f2a800;
  --hm-ink: #1c1511;
  --hm-muted: #6e6259;
  --hm-paper: #faf6ef;
  --hm-paper-2: #f3ecdf;
  --hm-line: rgba(28, 21, 17, 0.12);
  --hm-header-h: 64px;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: 80px;
}

body {
  margin: 0;
  background: var(--hm-paper);
  color: var(--hm-ink);
  font-family: "Instrument Sans", Inter, system-ui, -apple-system, "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

button,
input,
textarea,
select {
  font: inherit;
}

img {
  max-width: 100%;
}

::selection {
  background: var(--hm-red);
  color: #fff;
}

#app {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  flex-direction: column;
  width: 100%;
}

/* Lien d'évitement (accessibilité clavier) */
.skip-link {
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1000;
  padding: 10px 16px;
  transform: translateY(-160%);
  border-radius: 10px;
  background: var(--hm-ink);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease;
}
.skip-link:focus {
  transform: translateY(0);
}

/* En-tête des pages intérieures */
.app-header {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 150;
  display: flex;
  height: var(--hm-header-h);
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 clamp(16px, 4vw, 48px);
  background: rgba(250, 246, 239, 0.88);
  border-bottom: 1px solid transparent;
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}
.app-header.scrolled {
  border-bottom-color: var(--hm-line);
  box-shadow: 0 6px 24px rgba(28, 21, 17, 0.05);
}

.logo {
  display: block;
  width: auto;
  height: 38px;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.logo:hover {
  transform: scale(1.04);
}
.logo:focus-visible {
  outline: 3px solid var(--hm-orange);
  outline-offset: 4px;
  border-radius: 8px;
}

/* Contenu */
.app-main {
  flex: 1;
  width: 100%;
  min-width: 0;
}
.app-main.with-header {
  padding-top: var(--hm-header-h);
}
.app-main.with-tabs {
  padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
}

/* Tab bar */
.tabbar {
  position: fixed;
  left: 50%;
  bottom: calc(14px + env(safe-area-inset-bottom, 0px));
  z-index: 160;
  display: flex;
  gap: 6px;
  padding: 6px;
  transform: translateX(-50%);
  border: 1px solid var(--hm-line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
  box-shadow: 0 10px 34px rgba(28, 21, 17, 0.16);
}

.tab {
  display: grid;
  width: 52px;
  height: 52px;
  place-items: center;
  border-radius: 50%;
  color: var(--hm-muted);
  text-decoration: none;
  transition: background 0.25s ease, color 0.25s ease, transform 0.25s ease;
}
.tab:hover {
  background: var(--hm-paper-2);
  color: var(--hm-ink);
}
.tab.active {
  background: var(--hm-ink);
  color: #fff;
}
.tab:active {
  transform: scale(0.92);
}
.tab:focus-visible {
  outline: 3px solid var(--hm-orange);
  outline-offset: 2px;
}

/* Transition entre pages */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(14px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 600px) {
  :root {
    --hm-header-h: 58px;
  }
  .logo {
    height: 32px;
  }
  .tabbar {
    left: 12px;
    right: 12px;
    transform: none;
    justify-content: space-around;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>